/**
 * payout — one saved payslip, and the lifecycle it moves through.
 *
 * WHAT IT REPLACES. A row on RECORDS OF PAYOUT (or NON-BIR RECORDS) plus the
 * copied PAYSLIP worksheet that Module1 saved next to it. The workbook kept
 * those two apart: the records row carried nine columns, the worksheet copy
 * carried the lines, and once the copy was filed nothing joined them again —
 * so "why was this ₱9,457.60?" could only be answered by opening a file in a
 * OneDrive folder and hoping it was still there. Here the lines travel WITH
 * the record, so a payout can always show its own arithmetic.
 *
 * LINES ARE ONE JSON COLUMN, not a second list. A weekly run is ~57 payouts
 * with a handful of priced bands each; as rows that is ~20k list items a year
 * for a table nothing queries across, on a tenant that has already been bitten
 * once by indexing a list past 20k (see Isyn2ProvisioningService's header).
 * The same call was made for `iSyn2_DispatchSlips.wipIds`, and for the same
 * reason. The cost is that "how many CARGO 6-POCKET sets did we pay for in
 * August" is computed in the app rather than by the list — which is what the
 * Records screen does.
 *
 * STATUS IS FOUR VALUES AND THEY MEAN DIFFERENT THINGS:
 *
 *   Draft   derived, editable, on nobody's payslip yet. Nothing is owed.
 *   Issued  the payslip has been produced and the amount is owed. This is
 *           what the bank file draws from and what the records sheet shows.
 *   Paid    the money left. The workbook's REMARKS (PAID) checkbox, which
 *           was a bare TRUE/FALSE with no date and no name against it.
 *   Void    issued in error. Kept, never deleted — a payout reference that
 *           has been handed to somebody has to keep resolving to something,
 *           and a gap in the sequence is the one thing an auditor will ask
 *           about. Its amount counts nowhere.
 *
 * Pure and I/O-free.
 */
import type {
  BirSide, IPayoutAdditions, IPayoutDeductions, IPayoutLine, IPayoutTotals,
} from "./payrollMath";
import { payoutTotals, payslipSets, transpoFor } from "./payrollMath";
import { isOpen } from "./payrollStage";
import type { PaymentMode } from "./payrollProfile";

export const PAYOUT_STATUSES = ["Draft", "Issued", "Paid", "Void"] as const;
export type PayoutStatus = (typeof PAYOUT_STATUSES)[number];

export interface IPayout {
  spId?: number;
  /** "63-6/14/2025" — the join key for the records sheet, the payslip PDF
   *  and the bank file. Unique per contractor per period. */
  payoutRef: string;
  /** Ledger key, the same one BQC3/BQC5 use. */
  sewer: string;
  contractorId: string;
  contractorName: string;
  companyName?: string;
  /** BIR CoR / TIN, snapshotted at issue — the payslip prints it, and a
   *  profile edited later must not rewrite a document already handed over. */
  tin?: string;
  bir: BirSide;
  section?: string;
  /** Working period — PAYSLIP!E5 and F5. */
  periodStart: string;
  periodEnd: string;
  /** PAYSLIP!K5 — the day the payslip is dated and the bank batch is for. */
  dateIssued: string;
  modeOfPayment: PaymentMode;
  /** The account name the bank file uses, snapshotted at issue. */
  bankName?: string;
  bankAccount?: string;
  /** The masterlist's SCHEDULE column, snapshotted — "SCHED PAY OUT". */
  schedPayout?: string;
  lines: IPayoutLine[];
  additions: IPayoutAdditions;
  deductions: IPayoutDeductions;
  status: PayoutStatus;
  /** Free text — the records sheet's own REMARKS column. */
  remarks?: string;
  createdBy?: string;
  createdAt?: string;
  issuedBy?: string;
  issuedAt?: string;
  paidBy?: string;
  paidAt?: string;
  voidedBy?: string;
  voidedAt?: string;
  voidReason?: string;
}

/**
 * A payout with the allowances it has EARNED but was never told about.
 *
 * Mikko, 2026-09-15, holding a 55-set payslip: "where's transportation
 * allowance here the Php 200 for minimum of 20 sets?" It was not there
 * because `transpoFor` runs on the Subcon Payroll row and is written when you
 * press Pay — and that record was written before the rule existed. The
 * payslip screen prints what is stored, so it printed a zero.
 *
 * THIS IS THE MODULE'S OWN RULE, NOT A NEW ONE. An OPEN payout's figures
 * derive; a PAID one's are frozen at the moment the money moved
 * (payrollStage.ts). So an open payout earns the allowance its own delivered
 * sets earn, and a paid or void one is returned untouched — a payslip
 * somebody is holding must never gain ₱200 on a reprint.
 *
 * The count is `payslipSets`, the same figure the WORK COMPLETED heading
 * prints, because a stored payout does not carry `setsDelivered` — only its
 * lines. Exact for every payslip that is all full sets, all top only or all
 * pants only.
 *
 * ONE WART, STATED RATHER THAN HIDDEN: a clerk who deliberately zeroes the
 * allowance on an OPEN payslip will see it derived again next time it loads,
 * because nothing distinguishes "chosen to withhold" from "written before the
 * rule". Paying the payslip freezes whatever is on it, which is the escape.
 */
export function withEarnedAllowances(p: IPayout): IPayout {
  if (!isOpen(p.status)) return p;
  if (Number(p.additions.transpoAllowance) > 0) return p;
  const earned = transpoFor(payslipSets(p.lines));
  return earned > 0 ? { ...p, additions: { ...p.additions, transpoAllowance: earned } } : p;
}

/**
 * A payout's own arithmetic, recomputed from its lines every time.
 *
 * DERIVED FOR DISPLAY, always. A stored total drifts from the lines that are
 * supposed to justify it, and on a payslip the drift is money — so every
 * screen, the printed payslip and the bank file all call THIS.
 *
 * The list does also carry the four headline figures as plain columns
 * (`qtyTotal`, `workTotal`, `netAmount`, `totalAmount`), written at save
 * time. That is a snapshot for the benefit of anyone reading the SharePoint
 * list directly — the workbook's RECORDS OF PAYOUT had an AMOUNT column and
 * people will look for it — and it is never the source of a figure this app
 * shows. `driftOf` is what says so out loud when the two disagree.
 */
export function totalsOf(p: Pick<IPayout, "lines" | "bir" | "additions" | "deductions">): IPayoutTotals {
  return payoutTotals(p.lines, p.bir, p.additions, p.deductions);
}

/**
 * How far a stored snapshot has drifted from the lines, in pesos.
 *
 * Zero on every payout this app has written. A non-zero figure means the row
 * was edited outside the app (a SharePoint list edit, a Power Automate flow),
 * and the honest response is to show both numbers rather than to pick one —
 * see the Records screen.
 */
export function driftOf(p: IPayout, snapshotTotal: number | null | undefined): number {
  if (snapshotTotal === null || snapshotTotal === undefined) return 0;
  return Math.round((totalsOf(p).totalAmount - Number(snapshotTotal)) * 100) / 100;
}

/** Statuses whose amount is real money owed or moved. Void and Draft are
 *  neither, and every roll-up on the Records screen filters on this. */
export function countsAsPayable(status: PayoutStatus): boolean {
  return status === "Issued" || status === "Paid";
}

/** Which transitions are allowed, and nothing else. A Paid payout cannot go
 *  back to Draft: the money has left, and the record of a mistake is a Void
 *  plus a fresh payout, not a rewritten history. */
export const ALLOWED_TRANSITIONS: Readonly<Record<PayoutStatus, readonly PayoutStatus[]>> = {
  Draft: ["Issued", "Void"],
  Issued: ["Paid", "Void", "Draft"],
  Paid: ["Void"],
  Void: [],
};

export function canTransition(from: PayoutStatus, to: PayoutStatus): boolean {
  return ALLOWED_TRANSITIONS[from].includes(to);
}

/**
 * Two payouts are the same payout when their references match.
 *
 * The reference is contractor + period end, so re-running a week that has
 * already been issued produces the SAME reference rather than a second one —
 * which is exactly what makes a duplicate detectable. `existingByRef` is what
 * the run screen checks before it saves anything.
 */
export function existingByRef(payouts: readonly IPayout[]): Map<string, IPayout> {
  const m = new Map<string, IPayout>();
  for (const p of payouts) {
    if (!p.payoutRef) continue;
    const cur = m.get(p.payoutRef);
    // A VOID NEVER SHADOWS A LIVE PAYOUT. Once a week has been voided and paid
    // again, two records share the reference, and the one the screens mean is
    // the one that stands: a screen reading the void would show the payslip as
    // open and offer to pay work that has already been paid for. A void only
    // answers for a reference when every record under it is voided — which is
    // the reopened week, and exactly when the screen should say so.
    if (!cur || p.status !== "Void" || cur.status === "Void") m.set(p.payoutRef, p);
  }
  return m;
}

/** Payouts whose ISSUE date falls on a given day — what the bank batch and
 *  the workbook's Module12 both select on. */
export function issuedOn(payouts: readonly IPayout[], isoDate: string): IPayout[] {
  return payouts.filter(p => countsAsPayable(p.status) && p.dateIssued?.slice(0, 10) === isoDate);
}

/** Payouts covering a working period, whatever their status. */
export function forPeriod(payouts: readonly IPayout[], startIso: string, endIso: string): IPayout[] {
  return payouts.filter(p => p.periodEnd >= startIso && p.periodEnd <= endIso);
}
